import type { PlanogramShelfDividers } from "@CommonTypes/merchflow/pog/shelf";
import {
  getBetweenFacingsDividerWidth,
  getShelfItemDividerSlots,
  type DividerItemLike,
  type ShelfPiece,
} from "@CommonUtils/pog/shelfDividers";
import { canCombineFromSides, canMergeLeft, canMergeRight } from "./canCombine";

export type ShelfItemOverflowPolicy = "compress-to-boundary" | "extend-on-overflow";

/** A strip of a shelf no product may occupy (a pole, a duct), in shelf-relative x. */
export interface ShelfDeadSpan {
  x: number;
  width: number;
}

/** Where a board's usable part begins: the end of any dead spans that cover its left end. */
export const liveStartOf = (deadSpans: readonly ShelfDeadSpan[] | null | undefined): number => {
  let start = 0;
  for (const span of [...(deadSpans ?? [])].sort((a, b) => a.x - b.x)) {
    if (span.x > start) break;
    start = Math.max(start, span.x + span.width);
  }
  return start;
};

export interface ShelfWidthLike {
  width: number;
  merchandisableWidth?: number | null;
  deadSpans?: readonly ShelfDeadSpan[] | null;
}

export interface ShelfItemWidthLike {
  getBlockWidth: () => number;
  getXCappingWidth: () => number;
  getHorizontalFacings?: () => number;
  shelf?: { dividers?: PlanogramShelfDividers | null } | null;
}

/**
 * How much shelf a position occupies: the block of product, the caps standing
 * beside it and, on a shelf with dividers between facings, one divider per
 * facing column. A run's end dividers are in {@link getShelfItemLayoutWidths}.
 */
export const getShelfItemWidth = (item: ShelfItemWidthLike) => {
  const width = item.getBlockWidth() + item.getXCappingWidth();
  const dividerWidth = getBetweenFacingsDividerWidth(item.shelf?.dividers);
  return dividerWidth > 0 && item.getHorizontalFacings
    ? width + dividerWidth * item.getHorizontalFacings()
    : width;
};

/** The widths a run of items (one shelf or a merged group) lays out with, end dividers included. */
export const getShelfItemLayoutWidths = (items: readonly DividerItemLike[]): number[] => {
  const slots = getShelfItemDividerSlots(items);
  return slots ? slots.map((slot) => slot.width) : items.map(getShelfItemWidth);
};

/**
 * {@link getShelfItemLayoutWidths} and, on a divided shelf a pole cuts, the
 * piece each item stands in (DEV-3536): what `getShelfItemXOffsets` and
 * `itemsFitAroundDeadSpans` take as `pieces`.
 */
export const getShelfItemLayout = (items: readonly DividerItemLike[]) => {
  const slots = getShelfItemDividerSlots(items);
  return {
    itemWidths: slots ? slots.map((slot) => slot.width) : items.map(getShelfItemWidth),
    pieces: slots?.map((slot) => slot.piece),
  };
};

type ShelfItemPieces = readonly (ShelfPiece | undefined)[] | null | undefined;

/** Lengths closer than this are equal: pog widths are x100 floats. */
const PIECE_EPSILON = 1e-6;

/** Consecutive items standing in one piece: [first index, last index, piece]. */
const groupByPiece = (
  itemCount: number,
  pieces: ShelfItemPieces,
): [number, number, ShelfPiece][] | null => {
  if (!pieces || pieces.length !== itemCount || itemCount === 0) return null;
  const groups: [number, number, ShelfPiece][] = [];
  for (let index = 0; index < itemCount; index += 1) {
    const piece = pieces[index];
    if (!piece) return null;
    const group = groups[groups.length - 1];
    if (group && group[2] === piece) {
      group[1] = index;
    } else {
      groups.push([index, index, piece]);
    }
  }
  return groups;
};

/**
 * How much of a shelf its strips take, overlaps counted once. The spans are
 * clipped to the board and swept left to right, so two poles whose strips
 * overlap — or meet exactly — cost the shelf their union, never their sum.
 */
export const getDeadSpansUnionWidth = (
  spans: readonly ShelfDeadSpan[] | null | undefined,
  width: number,
): number => {
  const clipped = (spans ?? [])
    .map((span) => ({
      start: Math.max(0, span.x),
      end: Math.min(width, span.x + span.width),
    }))
    .filter(({ start, end }) => Number.isFinite(start) && Number.isFinite(end) && end > start)
    .sort((left, right) => left.start - right.start);

  let total = 0;
  let covered = 0;
  for (const { start, end } of clipped) {
    const from = Math.max(start, covered);
    if (end > from) {
      total += end - from;
      covered = end;
    }
  }
  return total;
};

/**
 * What a shelf can actually hold. Strips are the input of record: where a shelf
 * states them the width follows them and the stored scalar is ignored, so the
 * two representations cannot disagree. A shelf with no strips keeps the scalar
 * it was given — a shorter width with no strips is legacy input, and inventing
 * a right-end strip for it here would fabricate geometry nobody recorded.
 *
 * The one scalar the strips never override is zero. A shelf that holds no
 * product (a riser) is width 0 whatever its strips say, so strips left beside
 * it cannot hand it capacity; and a shelf obstructed end to end is width 0 with
 * one full-width strip, which reads 0 either way.
 */
export const getEffectiveMerchandisableWidth = (shelf: ShelfWidthLike) => {
  const { deadSpans, merchandisableWidth, width } = shelf;
  if (merchandisableWidth === 0) return 0;
  if (deadSpans?.length) {
    return Math.max(0, width - getDeadSpansUnionWidth(deadSpans, width));
  }
  return Math.max(0, Math.min(merchandisableWidth ?? width, width));
};

/**
 * Merged shelf groups are only valid when every member is fully merchandisable
 * (effectiveMerchandisableWidth === width). Partial and zero-width shelves must
 * not be merged, so this is the single gate merge/unmerge call sites rely on.
 */
export const isFullyMerchandisable = (shelf: ShelfWidthLike) =>
  getEffectiveMerchandisableWidth(shelf) === shelf.width;

export interface ShelfResizeState {
  merchandisableWidth?: number | null;
  width: number;
}

/**
 * Applies the agreed resize semantics for shelf width vs. merchandisableWidth:
 * - a fully merchandisable shelf stays fully merchandisable at the new width
 * - a partial/zero shelf keeps its absolute merchandisable amount, clamped to
 *   the new width (so shrinking below it makes the shelf fully merchandisable
 *   again, and it can never exceed the new physical width)
 * - shelves with no explicit merchandisableWidth (null/undefined) remain so,
 *   since that already means "full physical width" at any width
 */
export const resizeShelfWidth = (
  shelf: ShelfResizeState & { deadSpans?: readonly ShelfDeadSpan[] | null },
  newWidth: number,
): number | null | undefined => {
  if (shelf.deadSpans?.length) {
    const kept = resizeShelfDeadSpans(shelf.deadSpans, newWidth);
    return newWidth - getDeadSpansUnionWidth(kept, newWidth);
  }
  const { merchandisableWidth, width: previousWidth } = shelf;
  if (merchandisableWidth === null || merchandisableWidth === undefined) {
    return merchandisableWidth;
  }

  const wasFullyMerchandisable = getEffectiveMerchandisableWidth(shelf) === previousWidth;
  return wasFullyMerchandisable ? newWidth : Math.min(merchandisableWidth, newWidth);
};

/** The strips a shelf keeps when it is resized: an obstruction does not move, so clip at the new end. */
export const resizeShelfDeadSpans = (
  spans: readonly ShelfDeadSpan[] | null | undefined,
  newWidth: number,
): ShelfDeadSpan[] =>
  (spans ?? [])
    .map((span) => ({ x: span.x, width: Math.min(span.x + span.width, newWidth) - span.x }))
    .filter((span) => span.width > 0);

/** A shelf whose strips come from an obstruction: its merchandisable width follows them. */
export const hasObstructionSpans = (shelf: { deadSpans?: readonly ShelfDeadSpan[] | null }) =>
  (shelf.deadSpans?.length ?? 0) > 0;

/**
 * Where the shelf cannot hold products, left to right. Explicit spans win; a
 * shelf that only states a shorter merchandisable width (every pog before
 * 3.10) keeps its dead strip at the right end, as it has always been drawn.
 */
export const getDeadSpans = (shelf: ShelfWidthLike): ShelfDeadSpan[] => {
  if (shelf.deadSpans?.length) {
    return [...shelf.deadSpans].sort((left, right) => left.x - right.x);
  }
  const merchandisableWidth = getEffectiveMerchandisableWidth(shelf);
  return merchandisableWidth < shelf.width
    ? [{ x: merchandisableWidth, width: shelf.width - merchandisableWidth }]
    : [];
};

/**
 * Where a shelf can hold products, left to right, in shelf-relative x: the
 * board minus its dead spans (`getDeadSpans`). Empty for a shelf that holds
 * nothing (merchandisable width 0).
 */
export const getLiveSpans = (shelf: ShelfWidthLike): ShelfDeadSpan[] => {
  if (getEffectiveMerchandisableWidth(shelf) === 0) return [];
  const spans: ShelfDeadSpan[] = [];
  let x = 0;
  for (const span of getDeadSpans(shelf)) {
    const start = Math.min(Math.max(0, span.x), shelf.width);
    if (start > x) spans.push({ x, width: start - x });
    x = Math.max(x, span.x + span.width);
  }
  if (x < shelf.width) spans.push({ x, width: shelf.width - x });
  return spans;
};

/** True when nothing dead touches that edge, so a neighbour may merge onto it. */
export const isMergeableOnSide = (shelf: ShelfWidthLike, side: "left" | "right") =>
  getEffectiveMerchandisableWidth(shelf) > 0 &&
  !getDeadSpans(shelf).some((span) =>
    side === "left" ? span.x <= 0 : span.x + span.width >= shelf.width,
  );

export const restrictCanCombine = (
  canCombine: number | null | undefined,
  shelf: ShelfWidthLike,
) =>
  canCombineFromSides(
    canMergeLeft(canCombine) && isMergeableOnSide(shelf, "left"),
    canMergeRight(canCombine) && isMergeableOnSide(shelf, "right"),
  );

export const getMergedMerchandisableWidth = (shelves: readonly ShelfWidthLike[]) =>
  shelves.reduce((total, shelf) => total + getEffectiveMerchandisableWidth(shelf), 0);

/** Dead spans of a merged run, relative to the run's left edge. */
export const getMergedDeadSpans = (shelves: readonly ShelfWidthLike[]): ShelfDeadSpan[] => {
  const spans: ShelfDeadSpan[] = [];
  let offset = 0;
  for (const shelf of shelves) {
    for (const span of getDeadSpans(shelf)) {
      spans.push({ x: offset + span.x, width: span.width });
    }
    offset += shelf.width;
  }
  return spans;
};

export const getNonMerchandisableArea = (shelf: ShelfWidthLike) => {
  const xOffset = getEffectiveMerchandisableWidth(shelf);

  return {
    width: shelf.width - xOffset,
    xOffset,
  };
};

export const getMergedNonMerchandisableArea = (shelves: readonly ShelfWidthLike[]) => {
  const width = shelves.reduce((total, shelf) => total + shelf.width, 0);
  const xOffset = getMergedMerchandisableWidth(shelves);

  return {
    width: width - xOffset,
    xOffset,
  };
};

/**
 * Lays items out left to right, `itemGap` apart, jumping any item that would
 * overlap a strip to the strip's far side. `spans` must be sorted by x.
 * Returns each item's offset and where the last one ends.
 */
const layOutAroundSpans = (
  itemWidths: readonly number[],
  spans: readonly ShelfDeadSpan[],
  itemGap: number,
) => {
  let x = 0;
  const offsets = itemWidths.map((width) => {
    for (const span of spans) {
      if (x < span.x + span.width && x + width > span.x) {
        x = span.x + span.width;
      }
    }
    const offset = x;
    x += width + itemGap;
    return offset;
  });
  return { offsets, end: x - itemGap };
};

/**
 * Whether the items, in the order they stand, fit the free intervals a shelf's
 * strips leave. The aggregate (total width against merchandisable width) cannot
 * see this: a 70 on a 100 shelf with a strip at 40-50 is under the 90 it has in
 * total, yet fits neither the 40 left of the strip nor the 50 right of it.
 * Packed from the left is the least room an order-preserving layout takes, so
 * the items fit at all exactly when they fit that way. On a divided shelf a
 * pole cuts, an item's width depends on the piece it stands in, so the pieces
 * the divider rule gave them (`pieces`) are what must each hold their items.
 */
export const itemsFitAroundDeadSpans = ({
  itemWidths,
  width,
  deadSpans = [],
  pieces,
}: {
  itemWidths: readonly number[];
  /** The physical length the items stand on, in the same x frame as the strips. */
  width: number;
  deadSpans?: readonly ShelfDeadSpan[];
  /** Each item's piece, from {@link getShelfItemLayout}; ignored unless every item has one. */
  pieces?: ShelfItemPieces;
}) => {
  const groups = groupByPiece(itemWidths.length, pieces);
  if (groups) {
    return groups.every(([first, last, piece]) => {
      const total = itemWidths.slice(first, last + 1).reduce((sum, itemWidth) => sum + itemWidth, 0);
      return total <= piece.width + PIECE_EPSILON;
    });
  }
  const spans = [...deadSpans].sort((left, right) => left.x - right.x);
  return layOutAroundSpans(itemWidths, spans, 0).end <= width;
};

/**
 * Returns historical between-packed item offsets within the merchandising boundary.
 * The model compresses overflow to that boundary, while Konva preserves natural widths.
 * A non-positive merchandising boundary still lays out existing items from the origin;
 * this positioning-only fallback does not change the effective merchandisable width.
 */
export const getShelfItemXOffsets = ({
  itemWidths,
  merchandisableWidth,
  overflowPolicy,
  deadSpans = [],
  pieces,
}: {
  itemWidths: readonly number[];
  merchandisableWidth: number;
  overflowPolicy: ShelfItemOverflowPolicy;
  /** Strips no item may overlap, in the same x frame as the returned offsets. */
  deadSpans?: readonly ShelfDeadSpan[];
  /**
   * Each item's piece of a divided shelf a pole cuts ({@link getShelfItemLayout}).
   * When every item has one, each piece lays its own items out between its
   * edges, as the shelf would; otherwise ignored.
   */
  pieces?: ShelfItemPieces;
}) => {
  const groups = groupByPiece(itemWidths.length, pieces);
  if (groups) {
    const offsets: number[] = [];
    for (const [first, last, piece] of groups) {
      const widths = itemWidths.slice(first, last + 1);
      const total = widths.reduce((sum, itemWidth) => sum + itemWidth, 0);
      const rawPieceGap = widths.length > 1 ? (piece.width - total) / (widths.length - 1) : 0;
      const pieceGap =
        overflowPolicy === "extend-on-overflow" ? Math.max(0, rawPieceGap) : rawPieceGap;
      let x = piece.x;
      for (const itemWidth of widths) {
        offsets.push(x);
        x += itemWidth + pieceGap;
      }
    }
    return offsets;
  }

  const itemsWidth = itemWidths.reduce((total, width) => total + width, 0);
  const positioningBoundary = merchandisableWidth > 0 ? merchandisableWidth : itemsWidth;
  const rawGap =
    itemWidths.length > 1 ? (positioningBoundary - itemsWidth) / (itemWidths.length - 1) : 0;
  const gap = overflowPolicy === "extend-on-overflow" ? Math.max(0, rawGap) : rawGap;
  // A shelf with no merchandisable width is all dead; its items still lay out from the origin.
  const spans = merchandisableWidth > 0 ? deadSpans : [];
  const physicalEnd = positioningBoundary + spans.reduce((total, span) => total + span.width, 0);

  const layOut = (itemGap: number) => layOutAroundSpans(itemWidths, spans, itemGap);

  // Jumping a strip strands the space left of it. Take that out of the gaps,
  // not out of the shelf end, for as long as there is slack to give.
  let itemGap = gap;
  let layout = layOut(itemGap);
  for (let pass = 0; pass < 4 && spans.length > 0 && itemWidths.length > 1; pass += 1) {
    const overflow = layout.end - physicalEnd;
    if (overflow <= 0 || itemGap <= 0) break;
    itemGap = Math.max(0, itemGap - overflow / (itemWidths.length - 1));
    layout = layOut(itemGap);
  }
  // Each pass only takes out the overflow it saw, and a jump can strand more
  // space again, so the passes may run out with items still past the end.
  // Packed from the left, the same items in the same order take the least room
  // a layout can; where they fit at all, they fit that way.
  if (spans.length > 0 && itemGap > 0 && layout.end > physicalEnd) {
    layout = layOut(0);
  }
  return layout.offsets;
};
